/**
 * Authentication Token Management
 *
 * SECURITY WARNING: This module uses localStorage which is vulnerable to XSS attacks.
 * Migration to secureAuth.ts is in progress for enhanced security.
 *
 * @deprecated Use secureAuth.ts for new implementations
 */

import * as SecureAuth from './secureAuth';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_INFO_KEY = 'user_info';
const SAVED_LOGIN_ID_KEY = 'saved_login_id';

// Feature flag for secure storage migration
const USE_SECURE_STORAGE = process.env.REACT_APP_USE_SECURE_AUTH === 'true' || process.env.NODE_ENV === 'production';

export interface UserInfo {
  user_id: number;
  /** 로그인 아이디 — 이메일과 분리된 별도 식별자. */
  username: string;
  email: string;
  name: string;
  role?: string;
  /** 백엔드가 빌드해서 보내는 안정 경로. 헤더 아바타/작성자 칩에 사용. */
  profile_image_url?: string;
}

/**
 * Store authentication tokens
 * Uses secure storage in production, localStorage in development
 */
export const setTokens = (accessToken: string, refreshToken: string): void => {
  if (USE_SECURE_STORAGE) {
    // Use secure storage (memory + sessionStorage)
    SecureAuth.setTokens(accessToken, refreshToken);
  } else {
    // Legacy localStorage (development only)
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

/**
 * Get access token
 */
export const getAccessToken = (): string | null => {
  if (USE_SECURE_STORAGE) {
    return SecureAuth.getAccessToken();
  }
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Get refresh token
 */
export const getRefreshToken = (): string | null => {
  if (USE_SECURE_STORAGE) {
    return SecureAuth.getRefreshToken();
  }
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Clear all authentication data
 */
export const clearAuth = (): void => {
  if (USE_SECURE_STORAGE) {
    SecureAuth.clearAuth();
  }
  // Always clear localStorage to handle migration
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
};

/**
 * Store user information
 */
export const setUserInfo = (userInfo: UserInfo): void => {
  if (USE_SECURE_STORAGE) {
    SecureAuth.setUserInfo(userInfo);
  } else {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  }
};

/**
 * Get user information
 */
export const getUserInfo = (): UserInfo | null => {
  if (USE_SECURE_STORAGE) {
    return SecureAuth.getUserInfo();
  }

  const userInfoStr = localStorage.getItem(USER_INFO_KEY);
  if (!userInfoStr) return null;

  try {
    return JSON.parse(userInfoStr);
  } catch {
    return null;
  }
};

/**
 * Auto-login flag — secure 모드에선 토큰 localStorage 사본 보관 여부를 결정.
 * dev(localStorage) 모드는 원래 영속이므로 플래그만 기록된다.
 */
export const setAutoLogin = (enabled: boolean): void => {
  SecureAuth.setAutoLogin(enabled);
};

export const isAutoLoginEnabled = (): boolean => {
  return SecureAuth.isAutoLoginEnabled();
};

/**
 * 로그인 화면 "아이디 저장" — 로그인 아이디(username)만 localStorage 에 보관.
 */
export const setSavedLoginId = (id: string | null): void => {
  if (id) {
    localStorage.setItem(SAVED_LOGIN_ID_KEY, id);
  } else {
    localStorage.removeItem(SAVED_LOGIN_ID_KEY);
  }
};

export const getSavedLoginId = (): string | null => {
  return localStorage.getItem(SAVED_LOGIN_ID_KEY);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

/**
 * Initialize auth and handle migration from localStorage to secure storage
 * Should be called once when the app starts
 */
export const initializeAuth = (): void => {
  if (USE_SECURE_STORAGE) {
    // Check if we need to migrate from localStorage
    const oldAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const oldRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (oldAccessToken && oldRefreshToken && !isTokenExpired(oldRefreshToken)) {
      // Migrate tokens to secure storage
      SecureAuth.setTokens(oldAccessToken, oldRefreshToken);

      // Migrate user info
      const oldUserInfo = localStorage.getItem(USER_INFO_KEY);
      if (oldUserInfo) {
        try {
          const userInfo = JSON.parse(oldUserInfo);
          SecureAuth.setUserInfo(userInfo);
        } catch {
          // Invalid user info, ignore
        }
      }

      // Clear localStorage after migration
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_INFO_KEY);
    }

    // Initialize secure auth
    SecureAuth.initializeAuth();
  }
};
